import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"; // Added useLocation
import { API_ENDPOINT } from "../../utils/constants";
import useTemplateSettings from "../../hooks/useTemplateSettings";
import MuPb from "../../widgets/MuPb";
import {
  getAuthHeader,
  getContrastTextColor,
  getDbHeader,
  getErrorResponse,
  saveReg,
  toastError,
} from "../../utils/util";
import BannerSlider from "../templates/slider/BannerSlider";
import RegFooter from "../templates/RegFooter";
import CountryStateCity from "../widgets/CountryStateCity";
import MyDatePicker from "../datetime/MyDatePicker";
import SelectDropDown from "../SelectDropdown";
import ParagraphDescription from "../ParagraphDescription";
import MyTimePicker from "../datetime/MyTimePicker";
import MyDateRange from "../datetime/MyDateRange";
import { getIcon } from "../RegistrationForm";
import useGetFormEdit from "../../hooks/useGetFormEdit";
import CategorySelectorAccordions from "../widgets/CategorySelectorAccordions";
import { LoadingButton } from "@mui/lab";
import TermsConditions from "../templates/TermsConditions";
import { Close } from "@mui/icons-material";
import { toast } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";
import { MuiTelInput } from "mui-tel-input";
import SocialMediaInput from "../../widgets/SocialMediaInputs";
import { ClipboardCheck } from "lucide-react";

// ----------------------------------------------------------------------
// OPTIMIZATION 1: Move static variables and helper functions OUTSIDE the
// component so they are not recreated on every single keystroke/render.
// ----------------------------------------------------------------------

const DEFAULT_COUNTRY = { name: "India", code: "IN" };

function toCamelCase(str) {
  return str
    .replace(/[^a-zA-Z0-9\s_-]/g, "") // Remove special characters except spaces, underscores, and hyphens
    .toLowerCase()
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match, index) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+|[-_]+/g, ""); // Remove spaces, underscores, and hyphens
}

const validateForm = (fields) => {
  const errors = [];
  let isValid = true;

  fields.forEach((field) => {
    const properties = field.properties || {};
    const { label, value, required, specifyText } = properties;

    if (!required) return;
    let isInvalid = false;

    // Check for null or undefined
    if (value === null || value === undefined) {
      isInvalid = true;
    }
    // Check for empty string (after trimming)
    else if (typeof value === "string") {
      isInvalid = value.trim() === "";
    }
    // Check for empty array
    else if (Array.isArray(value)) {
      isInvalid = value.length === 0;
    }
    // Check for empty object
    else if (typeof value === "object") {
      isInvalid = Object.keys(value).length === 0;
    }

    if (
      field.label === "Country/State/City" &&
      (value?.state === null || value?.city === null)
    ) {
      isInvalid = true;
    }

    if (isInvalid) {
      isValid = false;
      errors.push(
        `${
          field?.label === "Country/State/City" ? field.label : label
        } is required`
      );
    } else {
      // Extended validation for "Other" and "Industry / Trade Association"
      if (value === "Other") {
        if (
          !specifyText ||
          typeof specifyText !== "string" ||
          specifyText.trim() === ""
        ) {
          isValid = false;
          errors.push(`Please specify the other for ${label}`);
        }
      } else if (value === "Industry / Trade Association") {
        if (
          !specifyText ||
          typeof specifyText !== "string" ||
          specifyText.trim() === ""
        ) {
          isValid = false;
          errors.push(`Please specify the association for ${label}`);
        }
      }
    }
  });

  return { isValid, errors };
};

// ----------------------------------------------------------------------
// FieldRenderer Memoized Component
// ----------------------------------------------------------------------
const FieldRenderer = React.memo(({ field, updateFieldProperties }) => {
  const { type, properties } = field;
  const handleChange = useCallback(
    (newV) => {
      updateFieldProperties(field.id, { value: newV });
    },
    [field.id, updateFieldProperties]
  );

  switch (type) {
    case "TextField":
      if (properties.label !== "Mobile No") {
        return (
          <TextField
            type={properties.type}
            label={properties.label}
            required={properties.required}
            multiline={properties.multiline}
            rows={properties.rows}
            value={properties.value || ""}
            onChange={(event) => handleChange(event.target.value)}
            InputProps={
              properties.iconed && properties.iconType
                ? {
                    startAdornment: (
                      <InputAdornment position="start">
                        {getIcon(properties.iconType, {
                          fontSize: "small",
                          color: "action",
                        })}
                      </InputAdornment>
                    ),
                  }
                : null
            }
            fullWidth
            variant="outlined"
            size="small"
          />
        );
      } else {
        return (
          <MuiTelInput
            size="small"
            defaultCountry="IN"
            onChange={handleChange}
            label={properties.label}
            required={properties.required}
            value={properties.value || ""}
          />
        );
      }
    case "PlainText":
      return (
        <Typography variant={properties.variant}>{properties.label}</Typography>
      );
    case "Checkbox":
      return (
        <FormControlLabel
          control={<Checkbox checked={properties.value || false} />}
          label={properties.label}
          required={properties.required}
          onChange={(event) => handleChange(event.target.checked)}
        />
      );
    case "Divider":
      return <Divider />;
    case "ParagraphDescription":
      return <ParagraphDescription properties={field.properties} />;
    case "RadioGroup": {
      const showSpecifyOther = properties.value === "Other";
      const showSpecifyAssociation =
        properties.value === "Industry / Trade Association";

      return (
        <FormControl component="fieldset" fullWidth>
          <Typography variant="subtitle1">
            {properties.label} {properties.required ? "*" : ""}
          </Typography>
          <RadioGroup
            row={properties.row}
            value={properties.value || ""}
            onChange={(event) => handleChange(event.target.value)}
          >
            {properties.options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
              />
            ))}
          </RadioGroup>
          {showSpecifyOther && (
            <TextField
              fullWidth
              size="small"
              label="Please specify the other"
              value={properties.specifyText || ""}
              onChange={(e) =>
                updateFieldProperties(field.id, { specifyText: e.target.value })
              }
              sx={{ mt: 1 }}
              required={properties.required}
            />
          )}
          {showSpecifyAssociation && (
            <TextField
              fullWidth
              size="small"
              label="Please specify the association"
              value={properties.specifyText || ""}
              onChange={(e) =>
                updateFieldProperties(field.id, { specifyText: e.target.value })
              }
              sx={{ mt: 1 }}
              required={properties.required}
            />
          )}
        </FormControl>
      );
    }
    case "Select": {
      const showSpecifyOther = properties.value === "Other";
      const showSpecifyAssociation =
        properties.value === "Industry / Trade Association";

      return (
        <Box sx={{ width: "100%" }}>
          <SelectDropDown
            value={properties.value || ""}
            onChange={handleChange}
            properties={properties}
          />
          {showSpecifyOther && (
            <TextField
              fullWidth
              size="small"
              label="Please specify the other"
              value={properties.specifyText || ""}
              onChange={(e) =>
                updateFieldProperties(field.id, { specifyText: e.target.value })
              }
              sx={{ mt: 1 }}
              required={properties.required}
            />
          )}
          {showSpecifyAssociation && (
            <TextField
              fullWidth
              size="small"
              label="Please specify the association"
              value={properties.specifyText || ""}
              onChange={(e) =>
                updateFieldProperties(field.id, { specifyText: e.target.value })
              }
              sx={{ mt: 1 }}
              required={properties.required}
            />
          )}
        </Box>
      );
    }
    case "ProductCategories":
      return (
        <CategorySelectorAccordions
          title={`${properties.label}${properties.required ? "*" : ""}`}
          onSelectionChange={handleChange}
        />
      );
    case "DatePicker":
      return (
        <MyDatePicker
          min={properties.minDate}
          max={properties.maxDate}
          label={properties.label}
          value={properties.value || null}
          onChange={handleChange}
        />
      );
    case "CountryStateCity":
      // OPTIMIZATION 2: Replaced local object with DEFAULT_COUNTRY constant
      return (
        <CountryStateCity
          key={properties.value?.country?.code || DEFAULT_COUNTRY.code}
          onChange={handleChange}
          initialCountry={properties.value?.country || DEFAULT_COUNTRY}
          countryRequired
          stateRequired
        />
      );
    case "TimePicker":
      return (
        <MyTimePicker
          label={properties.label}
          value={properties.value || null}
          onChange={handleChange}
        />
      );
    case "DateRange":
      return (
        <MyDateRange
          label={properties.label}
          value={properties.value || []}
          onChange={handleChange}
        />
      );
    case "Switch":
      return (
        <FormControlLabel
          control={
            <Switch
              checked={properties.value || false}
              onChange={(event) => handleChange(event.target.checked)}
            />
          }
          label={properties.labelText || "Active"}
        />
      );
    default:
      return <Typography variant="body2">{field.type}</Typography>;
  }
});

// ----------------------------------------------------------------------
// Main Container Component
// ----------------------------------------------------------------------
const RegFormContainer = ({ APP_ID }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation(); // Hook to get current path safely
  const tr = searchParams.get("tr");
  const id = APP_ID;
  const theme = useTheme();
  const navigate = useNavigate();

  // OPTIMIZATION 3: Memoize URL parsing so it only recalculates if the pathname changes,
  // rather than every time the user hits a key in a form field.
  const lastWord = useMemo(() => {
    const pathSegments = location.pathname
      .split("/")
      .filter((segment) => segment);
    const temp = pathSegments[pathSegments.length - 1];
    return temp === "moldex-fastnex" ? "moldex" : temp;
  }, [location.pathname]);

  const [open, setOpen] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { data, isLoading, isError, error } = useTemplateSettings(id);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const hasSetInitialData = useRef(false);
  const [socialData, setSocialData] = useState({});
  const [showPendingDialog, setShowPendingDialog] = useState(false);
  const {
    data: formItems,
    isLoading: isFormLoading,
    isError: isFormError,
    error: formError,
  } = useGetFormEdit(id, true);
  const [formFields, setFormFields] = useState([]);
  const templateSettings = data?.properties || {};

  const trackingPrefill = useMemo(() => {
    const trackingFieldId = data?.visitorRegTrackingKey;
    const trackingUrls = data?.visitorRegTrackingUrls;

    if (
      !tr ||
      !trackingFieldId ||
      !Array.isArray(trackingUrls) ||
      !Array.isArray(formItems?.form)
    ) {
      return null;
    }

    const trackedField = formItems.form.find(
      (field) => field.id === trackingFieldId
    );
    const options = trackedField?.properties?.options;
    const trackingIndex = trackingUrls.findIndex(
      (trackingValue) => String(trackingValue) === tr
    );
    const matchedOption =
      trackingIndex >= 0 && Array.isArray(options)
        ? options[trackingIndex]
        : undefined;

    if (matchedOption === undefined) return null;

    return {
      fieldId: trackingFieldId,
      option: matchedOption,
      value: trackedField.properties.isMultiSelect
        ? [matchedOption]
        : matchedOption,
    };
  }, [
    tr,
    data?.visitorRegTrackingKey,
    data?.visitorRegTrackingUrls,
    formItems?.form,
  ]);

  const sourceTracker = trackingPrefill?.option;

  useEffect(() => {
    if (formItems?.form && !isLoading && !hasSetInitialData.current) {
      setFormFields(
        formItems.form.map((field) =>
          field.id === trackingPrefill?.fieldId
            ? {
                ...field,
                properties: {
                  ...field.properties,
                  value: trackingPrefill.value,
                },
              }
            : field
        )
      );

      // Update site title & description if present
      if (formItems?.siteTitle) {
        document.title = formItems.siteTitle;
      }

      const metaDescription = document.querySelector(
        'meta[name="description"]'
      );

      if (metaDescription && formItems?.siteDescription) {
        metaDescription.setAttribute("content", formItems.siteDescription);
      }

      hasSetInitialData.current = true;
    }
  }, [
    formItems?.form,
    formItems?.siteTitle,
    formItems?.siteDescription,
    isLoading,
    trackingPrefill,
  ]);

  const updateFieldProperties = useCallback((fieldId, newProperties) => {
    setFormFields((prevFields) =>
      prevFields.map((field) =>
        field.id === fieldId
          ? {
              ...field,
              properties: { ...field.properties, ...newProperties },
            }
          : field
      )
    );
  }, []);

  const bannerAds = useMemo(() => {
    return lastWord === "moldex"
      ? data?.moldexSliders ?? []
      : templateSettings?.headerSliders ?? [];
  }, [lastWord, data?.moldexSliders, templateSettings?.headerSliders]);

  // 2. Determine if the banner should show
  const showVisitorBanner =
    formItems?.template === "template1" && formItems?.showBannerAdsVisitorReg;
  const showDelegateBanner =
    formItems?.template === "td" && formItems?.showBannerAdsTradeDelegateReg;
  const shouldShowBanner = showVisitorBanner || showDelegateBanner;

  // OPTIMIZATION 4: Converted to useMemo to avoid running evaluating screen size logic
  // repeatedly on every render.
  const headerHeight = useMemo(() => {
    if (isSmallScreen) return "150px";
    if (isMediumScreen) return "200px";
    if (isLargeScreen) return "300px";
    return "150px";
  }, [isSmallScreen, isMediumScreen, isLargeScreen]);

  const headerVariant = useMemo(() => {
    if (isSmallScreen) return "h6";
    if (isMediumScreen) return "h5";
    if (isLargeScreen) return "h4";
    return "h6";
  }, [isSmallScreen, isMediumScreen, isLargeScreen]);

  const computeSubmitData = useCallback(() => {
    const data = formFields.reduce((acc, f) => {
      const props = f.properties;
      const val = props.value;

      if (f.type === "CountryStateCity") {
        acc.country = val.country;
        acc.state = val.state;
        acc.city = val.city;
      }

      if (val !== undefined && props.label) {
        const id = toCamelCase(props.label);

        // Overwrite the submitted value with the specified text instead of creating a new key
        if (
          (val === "Other" || val === "Industry / Trade Association") &&
          props.specifyText
        ) {
          acc[id] = props.specifyText;
        } else {
          acc[id] = val;
        }
      }

      return acc;
    }, {});

    return { ...data, ...socialData };
  }, [formFields, socialData]);

  const handleSubmitRegForm = useCallback(async () => {
    setOpen(false);
    try {
      setRegistering(true);
      const submitData = computeSubmitData();
      const response = await axios.post(
        `${API_ENDPOINT}/event/visitor-register`,
        {
          ...submitData,
          eventVisitor: lastWord,
          ...(sourceTracker ? { sourceTracker } : {}),
        },
        getAuthHeader(id)
      );
      toast.success(response.data.message);
      let reg_id = response.data?.reg_id;

      if (reg_id) {
        saveReg(reg_id);
        navigate(`/registration-confirmation`);
      } else {
        setShowPendingDialog(true);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setRegistering(false);
    }
  }, [lastWord, computeSubmitData, id, sourceTracker, navigate]);

  if (isLoading || isFormLoading) {
    return <MuPb />;
  }
  if (isError) {
    return <Alert severity="error">{getErrorResponse(error)}</Alert>;
  }

  const handleValidationError = (errors) => {
    setValidationErrors(errors);
    setOpenErrorDialog(true);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        minHeight: "100vh",
        width: "100vw",
        overflow: "auto",
        position: "fixed",
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: headerHeight,
          top: 0,
          position: "absolute",
          backgroundImage: `url(${templateSettings.haderBg})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "cover",
        }}
      ></Box>
      <Box
        sx={{
          width: "100%",
          position: "absolute",
        }}
      >
        <Container maxWidth="md">
          <img
            src={
              lastWord === "moldex"
                ? data?.moldexHeader ?? ""
                : templateSettings.headerBanner
            }
            alt="Main Header"
            style={{
              marginTop: "18px",
              width: "100%",
              borderRadius: "4px",
              height: "auto",
              marginBottom: "20px",
            }}
          />
          {shouldShowBanner && <BannerSlider ADS={bannerAds} />}
          <Typography
            variant={headerVariant}
            component="h2"
            align="center"
            fontWeight={600}
            my={3}
            sx={{ color: "#E60008" }}
          >
            {formItems?.formTitle}
          </Typography>
          <Box>
            <Grid container spacing={2}>
              {formFields.map((field) => {
                const gridSize = field?.properties?.grid
                  ? parseInt(field.properties.grid, 10)
                  : 12;

                const responsiveGridSize = isSmallScreen
                  ? Math.max(6, gridSize)
                  : gridSize;

                return (
                  <Grid item xs={responsiveGridSize} key={field.id}>
                    <FieldRenderer
                      field={field}
                      updateFieldProperties={updateFieldProperties}
                    />
                  </Grid>
                );
              })}
            </Grid>
            {formItems?.properties?.includeSocialLinks && (
              <SocialMediaInput
                onChange={(obj) => {
                  setSocialData((prev) => ({
                    ...prev,
                    ...obj,
                  }));
                }}
                values={socialData}
              />
            )}
            <Box display={"flex"} justifyContent={"center"}>
              <LoadingButton
                loading={registering}
                onClick={() => {
                  const { isValid, errors } = validateForm(formFields);
                  if (!isValid) {
                    handleValidationError(errors);
                    return;
                  }
                  setOpen(true);
                }}
                sx={{
                  width: "300px",
                  bgcolor: data?.properties?.submitBgColor || "#e0e0e0",
                  color: getContrastTextColor(data?.properties?.submitBgColor),
                  my: 2,
                  "&:hover": {
                    bgcolor: data?.properties?.submitBgColor || "#e0e0e0",
                    filter: "brightness(0.9)",
                  },
                }}
                variant="outlined"
              >
                Submit
              </LoadingButton>
            </Box>
            <Dialog
              open={open}
              onClose={() => setOpen(false)}
              slotProps={{
                paper: {
                  sx: {
                    position: "relative",
                    overflow: "visible",
                  },
                },
              }}
            >
              <IconButton
                aria-label="close"
                onClick={() => setOpen(false)}
                sx={{
                  position: "absolute",
                  right: 0,
                  zIndex: 1,
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  boxShadow: 1,
                  width: 32,
                  height: 32,
                }}
              >
                <Close />
              </IconButton>

              <DialogContent>
                <TermsConditions
                  APP_ID={APP_ID}
                  onContinue={handleSubmitRegForm}
                />
              </DialogContent>
            </Dialog>

            <Dialog
              open={openErrorDialog}
              onClose={() => setOpenErrorDialog(false)}
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  minWidth: "400px",
                },
              }}
            >
              <DialogTitle
                sx={{ display: "flex", alignItems: "center", gap: 2 }}
              >
                <ErrorOutlineIcon color="error" fontSize="large" />
                <Typography variant="h6" component="div">
                  Please check the following
                </Typography>
              </DialogTitle>

              <DialogContent dividers>
                <List dense sx={{ py: 0 }}>
                  {validationErrors
                    .filter((item) => item !== "undefined is required")
                    .map((error, index) => (
                      <ListItem key={index} sx={{ alignItems: "flex-start" }}>
                        <ListItemIcon sx={{ minWidth: "32px", pt: "4px" }}>
                          <ErrorOutlineIcon color="error" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={error}
                          primaryTypographyProps={{
                            variant: "body1",
                            color: "text.primary",
                          }}
                        />
                      </ListItem>
                    ))}
                </List>
              </DialogContent>

              <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenErrorDialog(false)}
                  sx={{
                    borderRadius: "8px",
                    px: 3,
                    textTransform: "none",
                  }}
                >
                  Got it!
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={showPendingDialog}
              onClose={() => {}}
              PaperProps={{
                sx: {
                  borderRadius: "24px", // Softer, more modern corners
                  minWidth: "400px",
                  maxWidth: "450px",
                  p: 2, // Extra padding around the whole dialog
                  textAlign: "center", // Center-aligned for a confirmation modal feel
                  boxShadow: "0px 12px 40px rgba(0, 0, 0, 0.08)", // Softer, premium shadow
                  backgroundImage: "none",
                },
              }}
            >
              {/* Custom Header Area with Icon */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  pt: 3,
                  pb: 1,
                }}
              >
                <Box
                  sx={{
                    backgroundColor: theme.palette.primary.main + "15", // 15% opacity primary color
                    color: theme.palette.primary.main,
                    borderRadius: "50%",
                    p: 2.5,
                    mb: 2.5,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ClipboardCheck size={40} strokeWidth={1.5} />
                </Box>
                <Typography variant="h5" fontWeight="700" color="text.primary">
                  Registration Received
                </Typography>
              </Box>

              {/* Content Area */}
              <DialogContent sx={{ pb: 3, px: 4, overflowY: "visible" }}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2, lineHeight: 1.6 }}
                >
                  Thank you for your registration.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  You will receive a confirmation email along with your digital
                  badge at the earliest.
                </Typography>
              </DialogContent>

              {/* Actions Area */}
              <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => window.location.reload()}
                  disableElevation // Removes default MUI button drop-shadow for a flatter, modern look
                  sx={{
                    borderRadius: "12px", // Matches the dialog's rounded aesthetic
                    px: 6,
                    py: 1.2,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                    width: "80%", // Makes the button wider and more prominent
                  }}
                >
                  Got it
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        </Container>
        <RegFooter
          data={data}
          lastWord={lastWord}
          templateSettings={templateSettings}
        />
      </Box>
    </Box>
  );
};

export default RegFormContainer;
